/**
 * Cards block: card grid with selection, batch actions
 * Complex block with multi-select and batch action capabilities.
 *
 * Supports "Add to List" functionality for split panel feature:
 * - Each card can have an "Add to List" button
 * - Items added to SharedListStore for summary panel
 * - Button updates to "Added" state when item is in list
 */

import { registerBlockRenderer, getBlockRenderer, limitArray } from './registry';
import { escapeHtml, createBlockWrapper, handleImageError, disableButtonTemporarily } from './utils';
import type { CardGridData, CardData } from './types';
import { getListStore, type ListItem } from '../../core/list-store';
import { getDetailStore } from '../../core/detail-store';

// =============================================================================
// CARDS BLOCK
// =============================================================================

registerBlockRenderer('cards', (data: unknown, container: HTMLElement, onAction?: (value: string) => void) => {
  const cardsData = data as CardGridData;
  // SAFETY: Limit array size to prevent DOM explosion
  const cards = limitArray(cardsData.cards ?? [], 50);
  const selectable = cardsData.selectable ?? false;
  const batchActions = limitArray(cardsData.batchActions ?? [], 10);
  const selectHelpers = limitArray(cardsData.selectHelpers ?? [], 5);

  // Detect compact mode: cards without images (demo gallery style)
  const hasImages = cards.some(c => c?.image);
  const isCompact = !hasImages && cards.length <= 6;

  const wrapper = createBlockWrapper('cards');
  if (isCompact) {
    wrapper.classList.add('pms-block-cards-compact');
  }

  // Track selected cards
  const selectedCards = new Set<string>();

  // Select helpers (3, 5, 10)
  if (selectable && selectHelpers.length > 0) {
    const helpersWrapper = document.createElement('div');
    helpersWrapper.className = 'pms-select-helpers';

    const helpersLabel = document.createElement('span');
    helpersLabel.className = 'pms-select-helpers-label';
    helpersLabel.textContent = 'Quick select:';
    helpersWrapper.appendChild(helpersLabel);

    for (const count of selectHelpers) {
      const btn = document.createElement('button');
      btn.className = 'pms-select-helper-btn';
      btn.textContent = `${count}`;
      btn.addEventListener('click', () => {
        selectedCards.clear();
        const cardEls = grid.querySelectorAll('.pms-card');
        cardEls.forEach((el, idx) => {
          if (idx < count) {
            el.classList.add('pms-card-selected');
            const cardId = cards[idx]?.id ?? String(idx);
            selectedCards.add(cardId);
          } else {
            el.classList.remove('pms-card-selected');
          }
        });
        updateBatchBar();
      });
      helpersWrapper.appendChild(btn);
    }

    wrapper.appendChild(helpersWrapper);
  }

  const grid = document.createElement('div');
  grid.className = isCompact ? 'pms-cards-grid pms-cards-grid-compact' : 'pms-cards-grid';

  // Only show "Add to List" when cards have real IDs (API data, not Claude-generated info)
  const hasRealIds = cards.some(c => c?.id != null);

  for (let i = 0; i < cards.length; i++) {
    const card = cards[i];
    if (!card) continue;
    const cardId = card.id ?? String(i);
    const cardEl = createCardElement(card, cardId, selectable, onAction, hasRealIds, isCompact);

    if (selectable) {
      cardEl.addEventListener('click', (e) => {
        // Don't toggle if clicking an action button
        if ((e.target as HTMLElement).closest('.pms-card-action')) return;

        if (selectedCards.has(cardId)) {
          selectedCards.delete(cardId);
          cardEl.classList.remove('pms-card-selected');
        } else {
          selectedCards.add(cardId);
          cardEl.classList.add('pms-card-selected');
        }
        updateBatchBar();
      });
    }

    grid.appendChild(cardEl);
  }

  wrapper.appendChild(grid);

  // Batch actions bar
  let batchBar: HTMLElement | null = null;
  if (selectable && batchActions.length > 0) {
    batchBar = document.createElement('div');
    batchBar.className = 'pms-batch-actions';
    batchBar.style.display = 'none';

    const countSpan = document.createElement('span');
    countSpan.className = 'pms-batch-count';
    batchBar.appendChild(countSpan);

    for (const action of batchActions) {
      const btn = document.createElement('button');
      btn.className = `pms-batch-btn ${action.variant === 'primary' ? 'pms-batch-btn-primary' : ''}`;
      btn.textContent = action.label;
      btn.addEventListener('click', () => {
        if (onAction) {
          const selectedIds = Array.from(selectedCards).join(',');
          onAction(`${action.value}:${selectedIds}`);
        }
      });
      batchBar.appendChild(btn);
    }

    wrapper.appendChild(batchBar);
  }

  function updateBatchBar() {
    if (!batchBar) return;
    const count = selectedCards.size;
    if (count > 0) {
      batchBar.style.display = 'flex';
      const countSpan = batchBar.querySelector('.pms-batch-count');
      if (countSpan) {
        countSpan.textContent = `${count} selected`;
      }
    } else {
      batchBar.style.display = 'none';
    }
  }

  container.appendChild(wrapper);
});

/**
 * Create a single card element
 */
function createCardElement(
  card: CardData,
  cardId: string,
  selectable: boolean,
  onAction?: (value: string) => void,
  showAddToList: boolean = true,
  isCompact: boolean = false
): HTMLElement {
  const cardEl = document.createElement('div');
  cardEl.className = 'pms-card';
  cardEl.dataset['cardId'] = cardId;

  // Add actionable class only if card has an action or is selectable
  const hasAction = card.action && (card.action.url || card.action.value || card.action.label);
  if (selectable || hasAction) {
    cardEl.classList.add('pms-card-actionable');
  }

  if (selectable) {
    cardEl.classList.add('pms-card-selectable');
  }

  let html = '';

  // Selection checkbox
  if (selectable) {
    html += `<div class="pms-card-checkbox"><span class="pms-check-mark"></span></div>`;
  }

  if (card.image) {
    html += `<img class="pms-card-image" src="${escapeHtml(card.image)}" alt="" loading="lazy" decoding="async" />`;
  }

  if (card.badge) {
    html += `<div class="pms-card-badge">${escapeHtml(card.badge)}</div>`;
  } else if (isCompact) {
    // Default icon for compact cards without a badge, for visual consistency
    html += `<div class="pms-card-icon-default"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="28" height="28"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/></svg></div>`;
  }

  if (card.title) {
    html += `<div class="pms-card-title">${escapeHtml(card.title)}</div>`;
  }

  if (card.description) {
    html += `<div class="pms-card-description">${escapeHtml(card.description)}</div>`;
  }

  if (card.price) {
    html += `<div class="pms-card-price">${escapeHtml(card.price)}</div>`;
  }

  if (card.metadata) {
    // Filter and format metadata - skip bad values
    const filteredMeta = Object.entries(card.metadata).filter(([key, value]) => {
      // Skip if key or value is empty
      if (!key || !value) return false;
      const strValue = String(value).toLowerCase().trim();
      // Skip boolean-like values
      if (strValue === 'true' || strValue === 'false') return false;
      // Skip very short numeric-only values (like "2", "3", "4")
      if (/^\d{1,2}$/.test(strValue)) return false;
      // Skip generic keys that aren't useful
      const skipKeys = ['id', 'slug', 'created', 'updated', 'raw'];
      if (skipKeys.includes(key.toLowerCase())) return false;
      return true;
    });

    if (filteredMeta.length > 0) {
      html += '<div class="pms-card-metadata">';
      for (const [key, value] of filteredMeta) {
        // Format key: convert snake_case/kebab-case to Title Case
        const formattedKey = key
          .replace(/[-_]/g, ' ')
          .replace(/\b\w/g, c => c.toUpperCase());
        html += `<span class="pms-card-meta-item"><strong>${escapeHtml(formattedKey)}:</strong> ${escapeHtml(String(value))}</span>`;
      }
      html += '</div>';
    }
  }

  // Card actions row (existing action + add to list)
  const hasCardAction = card.action && !selectable;
  const canAddToList = showAddToList && card.title && !selectable;

  if (hasCardAction || canAddToList) {
    html += '<div class="pms-card-actions">';

    if (hasCardAction && card.action) {
      const actionUrl = card.action.url ?? '#';
      const actionValue = card.action.value ?? card.action.label;
      if (card.action.url) {
        html += `<a class="pms-card-action" href="${escapeHtml(actionUrl)}" target="_blank" rel="noopener">${escapeHtml(card.action.label)}</a>`;
      } else {
        html += `<button class="pms-card-action" data-action="${escapeHtml(actionValue)}">${escapeHtml(card.action.label)}</button>`;
      }
    }

    if (canAddToList) {
      const listStore = getListStore();
      const isInList = listStore.hasItem(cardId);
      html += `<button class="pms-card-add-list ${isInList ? 'pms-card-add-list-added' : ''}" data-card-id="${escapeHtml(cardId)}">
        <svg class="pms-card-add-list-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
          ${isInList
            ? '<polyline points="20 6 9 17 4 12"/>'
            : '<line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>'
          }
        </svg>
        <span class="pms-card-add-list-text">${isInList ? 'Added' : 'Add'}</span>
      </button>`;
    }

    html += '</div>';
  }

  cardEl.innerHTML = html;

  // Handle image load errors
  const cardImage = cardEl.querySelector('.pms-card-image') as HTMLImageElement | null;
  if (cardImage) {
    handleImageError(cardImage);
  }

  // Bind action button click with spam protection
  const actionBtn = cardEl.querySelector('.pms-card-action[data-action]') as HTMLButtonElement | null;
  if (actionBtn && onAction) {
    actionBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      disableButtonTemporarily(actionBtn, 2000);
      const action = actionBtn.dataset['action'];
      if (action) onAction(action);
    });
  }

  // Bind "Add to List" button
  const addListBtn = cardEl.querySelector('.pms-card-add-list') as HTMLButtonElement | null;
  if (addListBtn) {
    addListBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      handleAddToList(card, cardId, addListBtn, onAction);
    });
  }

  // Card body click opens detail view (skip if selectable mode)
  if (!selectable) {
    cardEl.addEventListener('click', (e) => {
      // Skip if clicking action buttons or add-to-list buttons
      if ((e.target as HTMLElement).closest('.pms-card-action')) return;
      if ((e.target as HTMLElement).closest('.pms-card-add-list')) return;

      // Open detail view
      const detailStore = getDetailStore();
      detailStore.setActiveItem({
        id: cardId,
        title: card.title || 'Untitled',
        description: card.description,
        image: card.image,
        images: card.images,
        badge: card.badge,
        price: card.price,
        metadata: card.metadata,
        stats: card.stats,
        features: card.features,
        action: card.action,
        actions: card.actions,
        sourceBlockType: 'cards',
      });

      // Notify container to open detail panel
      if (onAction) onAction(`detail:${cardId}`);
    });
  }

  return cardEl;
}

/**
 * Handle adding a card to the list.
 */
function handleAddToList(
  card: CardData,
  cardId: string,
  button: HTMLButtonElement,
  onAction?: (value: string) => void
): void {
  const listStore = getListStore();

  // If already in list, remove it
  if (listStore.hasItem(cardId)) {
    listStore.removeItem(cardId);
    button.classList.remove('pms-card-add-list-added');
    button.innerHTML = `
      <svg class="pms-card-add-list-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
      </svg>
      <span class="pms-card-add-list-text">Add</span>
    `;
    if (onAction) onAction(`list:remove:${cardId}`);
    return;
  }

  // Parse price from string (e.g., "$29.99" -> 2999, "£62,995" -> 62995)
  let priceValue: number | undefined;
  let currency = 'USD';
  if (card.price) {
    const priceMatch = card.price.match(/([£$€A-Z]{1,3})?([\d,.]+)/);
    if (priceMatch && priceMatch[2]) {
      const currencySymbol = priceMatch[1];
      priceValue = parseInt(priceMatch[2].replace(/[,.]/g, ''), 10);
      if (currencySymbol === '£') currency = 'GBP';
      else if (currencySymbol === '€') currency = 'EUR';
      else if (currencySymbol === 'A$') currency = 'AUD';
      else if (currencySymbol === 'C$') currency = 'CAD';
    }
  }

  // Determine item type from card data
  let itemType: ListItem['type'] = 'generic';
  const titleLower = (card.title || '').toLowerCase();
  const descLower = (card.description || '').toLowerCase();
  const badgeLower = (card.badge || '').toLowerCase();
  const metaType = card.metadata?.['type']?.toLowerCase() || '';
  const metaCategory = card.metadata?.['category']?.toLowerCase() || '';

  // Check badge and metadata for type hints
  const typeHints = `${badgeLower} ${metaType} ${metaCategory}`;

  if (typeHints.includes('player') || typeHints.includes('athlete') || typeHints.includes('coach')) {
    itemType = 'player';
  } else if (typeHints.includes('rune') || typeHints.includes('item') || typeHints.includes('build') || typeHints.includes('gear') || typeHints.includes('loadout')) {
    itemType = 'build';
  } else if (typeHints.includes('service') || typeHints.includes('appointment') || typeHints.includes('booking')) {
    itemType = 'service';
  } else if (titleLower.includes('car') || descLower.includes('vehicle') || descLower.includes('mileage') || typeHints.includes('vehicle')) {
    itemType = 'vehicle';
  } else if (titleLower.includes('house') || titleLower.includes('property') || descLower.includes('bedroom') || typeHints.includes('property')) {
    itemType = 'property';
  } else if (priceValue !== undefined) {
    itemType = 'product';
  }

  // Extract category from badge or metadata
  let category: string | undefined;
  if (card.badge) {
    category = card.badge.toLowerCase();
  } else if (card.metadata?.['category']) {
    category = card.metadata['category'].toLowerCase();
  } else if (card.metadata?.['type']) {
    category = card.metadata['type'].toLowerCase();
  }

  // Extract tags from metadata if present
  let tags: string[] | undefined;
  if (card.metadata?.['tags']) {
    // Tags might be comma-separated string or already in metadata
    const tagsStr = card.metadata['tags'];
    tags = tagsStr.split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
  }
  // Also add badge as a tag if present
  if (card.badge && !tags?.includes(card.badge.toLowerCase())) {
    tags = tags || [];
    tags.push(card.badge.toLowerCase());
  }

  // Add to list with category and tags
  listStore.addItem({
    id: cardId,
    type: itemType,
    title: card.title || 'Untitled',
    subtitle: card.description,
    price: priceValue,
    currency,
    image: card.image,
    category,
    tags,
    metadata: card.metadata,
  });

  // Update button appearance
  button.classList.add('pms-card-add-list-added');
  button.innerHTML = `
    <svg class="pms-card-add-list-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
    <span class="pms-card-add-list-text">Added</span>
  `;

  // Notify via action
  if (onAction) onAction(`list:add:${cardId}`);
}

// Also register as 'card-grid' for compatibility
registerBlockRenderer('card-grid', (data, container, onAction) => {
  const renderer = getBlockRenderer('cards');
  if (renderer) renderer(data, container, onAction);
});
